import { FormControl, FormLabel, Radio, Select, Stack } from '@chakra-ui/react'
import type { PersonAdditionalData } from 'BrokerTsLib/src/dtos'
import { Product, Selection, SelectionType } from 'BrokerTsLib/src/enums'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { useEffect } from 'react'

import RuianAddressSelector from '@/components/RuianAddressSelector'
import { useApp } from '@/hooks/useApp'
import type { Step3FormValues } from '../..'
import { useInsuranceParams } from '../../../../../../context/InsuranceParamsProvider'
import { useSelections } from '../../../../../../hooks'
import { useNameof } from '../../../../../../hooks/useNameof'
import { FormBody } from '../../../../../forms/FormBody'
import { FormRow } from '../../../../../forms/FormRow'
import ICOOptions from '../../../../../forms/person/ICOOptions'
import InsureeName from '../../../../../forms/person/InsureeName'
import PersonType from '../../../../../forms/person/PersonType'
import { RadioGroup } from '../../../../../forms/RadioGroup'

export const AdditionalContactInfoForm = () => {
  const { product } = useApp()
  const { isDisabled } = useInsuranceParams()

  const { watch, register, getValues, setValue } = useFormContext<Step3FormValues>()

  const nameof = useNameof<Step3FormValues>()
  const personPrefix = nameof(x => x.policyHolder.contact)
  const pnameof = useNameof<PersonAdditionalData>(personPrefix)

  const { data: dataTitles } = useSelections(SelectionType.TitleBefore)

  const contactSameAs = watch('policyHolder.contactSameAs')
  const ownerSameAs = watch('policyHolder.ownerSameAs')
  const holderSameAs = watch('policyHolder.holderSameAs')
  const pType = watch('policyHolder.contact.type')

  const sameAsOptions = [
    { label: 'Stejná s pojistníkem', value: Selection.PersonSameAsPolicyHolder },
    ...(product == Product.VehicleLeasingInsurance ? [{ label: 'Stejný s klientem', value: Selection.PersonSameAsClient }] : []),
    ...(!ownerSameAs || contactSameAs == Selection.PersonSameAsOwner ? [{ label: 'Stejná s vlastníkem', value: Selection.PersonSameAsOwner }] : []),
    ...(!holderSameAs || contactSameAs == Selection.PersonSameAsKeeper ? [{ label: 'Stejný s držitelem/provozovatelem', value: Selection.PersonSameAsKeeper }] : []),
    { label: 'Jiná', value: null },
  ]

  // nastavení default typu osoby pri prekliknuti
  useEffect(() => {
    if (!contactSameAs && !pType)
      setValue('policyHolder.contact.type', Selection.Person)
  }, [contactSameAs])

  return (
    <FormBody>
      <FormControl>
        <FormLabel>Korespondenční adresa</FormLabel>

        <RadioGroup
          {...register('policyHolder.contactSameAs', { setValueAs: (value) => value == 0 ? null : parseInt(value) })}
          value={contactSameAs?.toString() ?? '0'}
          isDisabled={isDisabled}
          onChange={(value) => setValue('policyHolder.contactSameAs', parseInt(value))}
        >
          <Stack direction="row" spacing={16}>
            {sameAsOptions?.map((item) => (
              <Radio key={item.value ?? -1} value={item.value?.toString() ?? '0'}>
                {item.label}
              </Radio>
            ))}
          </Stack>
        </RadioGroup>
      </FormControl>

      {!contactSameAs &&
        <>
          <PersonType personPrefix={personPrefix} disabled={isDisabled} />

          {([Selection.Company, Selection.SelfEmployed] as Selection[]).includes(pType) &&
            <ICOOptions personPrefix={personPrefix} disabled={isDisabled} hideIc />
          }

          {([Selection.Person, Selection.SelfEmployed] as Selection[]).includes(pType) &&
            <FormRow itemsCount={3}>
              <FormControl>
                <FormLabel>Titul před jménem</FormLabel>

                {(dataTitles?.data.length ?? 0) > 0 &&
                  <Select
                    {...register('policyHolder.contact.titleBefore')}
                    isDisabled={isDisabled}
                    defaultValue={getValues('policyHolder.contact.titleBefore') ?? ''}
                  >
                    <option value=''>Vyberte</option>
                    {dataTitles?.data.map((item) => (
                      <option value={item.value?.toString()} key={item.id}>{item.value}</option>
                    ))}
                  </Select>
                }
              </FormControl>

              <InsureeName personPrefix={personPrefix} disabled={isDisabled} />
            </FormRow>
          }

          <RuianAddressSelector
            addressPath={pnameof((x) => x.address)}
            isDisabled={isDisabled}
            showOnlyLotNumber={false}
          />
        </>
      }

    </FormBody>
  )
}